import { ReadonlyVec2, vec2 } from 'gl-matrix';
import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { useResidentEnvironments } from '~/hooks/environment/useResidentEnvironments';
import { Environment } from '~/engine/data/Environment';
import {
    DEFAULT_DOSE_OPACITY_VALUE,
    DEFAULT_DOSE_VISIBLE,
    DEFAULT_MAX_DOSE_RANGE,
    DEFAULT_MIN_DOSE_RANGE,
} from '~/engine/data/dose/DoseDistribution';
import {
    currentDoseIdState,
    doseOpacityState,
    doseVisibilityState,
    selectedDoseRangeState,
} from '~/state/atoms/dose/doseAtoms';

/** How a dose is shown: the colorwash window, the opacity, and whether at all. */
interface DoseDisplay {
    readonly visible: boolean;
    /** Colorwash alpha level, 0..1. */
    readonly opacity: number;
    /** Colorwash window [min, max], normalized to 0..1 of max dose. */
    readonly range: ReadonlyVec2;
}

/** What an Environment's dose shows when nothing governs it. */
const ENGINE_DEFAULT_DISPLAY: DoseDisplay = {
    visible: DEFAULT_DOSE_VISIBLE,
    opacity: DEFAULT_DOSE_OPACITY_VALUE,
    range: vec2.fromValues(DEFAULT_MIN_DOSE_RANGE, DEFAULT_MAX_DOSE_RANGE),
};

/**
 * Writes the display onto one Environment's dose, field by field and only
 * where the dose shows something else: every engine setter repaints the
 * dose layer, so an unchanged field costs no redraw. An Environment with
 * no dose has nothing to show and is left untouched. Returns whether the
 * Environment holds a dose the display was written to.
 */
function publishDoseDisplay(
    environment: Environment,
    display: DoseDisplay
): boolean {
    const dose = environment.dose;
    if (!dose) {
        return false;
    }
    if (dose.isVisible !== display.visible) {
        environment.updateDoseVisibility(display.visible);
    }
    if (dose.alphaLevelOpacity !== display.opacity) {
        environment.updateDoseAlphaLevelOpacity(display.opacity);
    }
    if (!vec2.exactEquals(dose.selectedDoseRange, display.range)) {
        // Cloned: the engine keeps the buffer it is handed, and the atom's
        // buffer is shared with every reader of the slider.
        environment.updateSelectedDoseRange(vec2.clone(display.range));
    }
    return true;
}

/**
 * Bridges the dose display atoms onto every Environment the viewport grid
 * draws, the registry's residents. Whatever writes the atoms reaches every
 * drawn dose through here, so no control needs an Environment of its own.
 * A dose that appears later, on a dose load or on a resident minted for a
 * new row, starts at the engine defaults and is brought to the atoms at
 * once. Only the drawn Environments hold the atoms: one that leaves the
 * drawn list is returned to the engine defaults at once and let go, and
 * every one still written is returned on unmount, so a later mount starts
 * from a known display and a released Environment is not held. Mount once,
 * in a component that stays rendered for as long as the viewer does.
 */
export function useDoseDisplayBridge(): void {
    const environments = useResidentEnvironments();
    const visible = useRecoilValue(doseVisibilityState);
    const opacity = useRecoilValue(doseOpacityState);
    const range = useRecoilValue(selectedDoseRangeState);
    const currentDoseId = useRecoilValue(currentDoseIdState);
    // The Environments this hook has written and not yet reset.
    const writtenRef = useRef(new Set<Environment>());

    // A dose load replaces an Environment's dose object with one at the
    // engine defaults without changing the Environment list, so the
    // selected dose id is what re-runs this when a dose is loaded or
    // switched. Without a selected dose nothing is drawn.
    useEffect(() => {
        const written = writtenRef.current;
        for (const environment of written) {
            if (!environments.includes(environment)) {
                publishDoseDisplay(environment, ENGINE_DEFAULT_DISPLAY);
                written.delete(environment);
            }
        }
        if (currentDoseId === null) {
            return;
        }
        const display: DoseDisplay = { visible, opacity, range };
        for (const environment of environments) {
            if (publishDoseDisplay(environment, display)) {
                written.add(environment);
            }
        }
    }, [environments, visible, opacity, range, currentDoseId]);

    // Reset on unmount only; the ref keeps this effect dep-free.
    useEffect(
        () => () => {
            const written = writtenRef.current;
            for (const environment of written) {
                publishDoseDisplay(environment, ENGINE_DEFAULT_DISPLAY);
            }
            written.clear();
        },
        []
    );
}
